import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Package } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const soonItems = [
    { name: "WMWE Mug", category: "Home" },
    { name: "WMWE Journal", category: "Stationery" },
    { name: "WMWE Tote", category: "Accessories" },
];

function WMWEProductCard({ product, onBuy }) {
    const sizes = product.sizes || [];
    const colors = product.colors || [];
    const [size, setSize] = useState(sizes[Math.min(2, sizes.length - 1)] || null);
    const [color, setColor] = useState(colors[0] || null);
    const [loading, setLoading] = useState(false);

    const buy = async (e) => {
        e.preventDefault();
        const form = e.target;
        const email = form.email.value;
        if (!email) {
            toast.error("Please enter your email.");
            return;
        }
        if (sizes.length && !size) {
            toast.error("Please select a size.");
            return;
        }
        setLoading(true);
        await onBuy(product, { quantity: 1, size, color, customer_email: email });
        setLoading(false);
    };

    return (
        <article
            className="bg-sanct-cream rounded-[24px] overflow-hidden flex flex-col border border-sanct-clay/40 shadow-sm"
            data-testid={`wmwe-product-${product.id}`}
        >
            <div className="relative aspect-square bg-sanct-sand overflow-hidden">
                <img src={product.image_url} alt={product.name} className="w-full h-full object-contain p-6" />
                <span className="absolute top-4 left-4 bg-sanct-cocoa text-sanct-cream text-[10px] uppercase tracking-[0.25em] px-3 py-1.5 rounded-full">
                    {product.category}
                </span>
            </div>
            <div className="p-8 flex-1 flex flex-col">
                <h3 className="font-serif text-2xl md:text-3xl text-sanct-cocoa leading-tight">{product.name}</h3>
                <p className="font-serif italic text-base text-sanct-rose mt-1">{product.tagline}</p>
                <p className="font-sans text-sm text-sanct-charcoal/80 mt-4 leading-[1.7] flex-1">{product.description}</p>
                <div className="flex items-baseline gap-2 mt-6 mb-5">
                    <span className="font-serif text-3xl text-sanct-cocoa">${product.price.toFixed(0)}</span>
                    <span className="font-sans text-xs text-sanct-charcoal/50 uppercase tracking-[0.2em]">USD</span>
                </div>

                {colors.length > 0 && (
                    <div className="mb-4">
                        <label className="font-sans text-[10px] uppercase tracking-[0.3em] text-sanct-rose block mb-2">Color</label>
                        <div className="flex flex-wrap gap-1.5">
                            {colors.map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setColor(c)}
                                    data-testid={`wmwe-product-${product.id}-color-${c}`}
                                    className={`px-3 h-9 text-xs font-medium border rounded-full transition-colors ${
                                        color === c
                                            ? "bg-sanct-cocoa text-sanct-cream border-sanct-cocoa"
                                            : "bg-transparent text-sanct-cocoa border-sanct-cocoa/30 hover:border-sanct-cocoa"
                                    }`}
                                >
                                    {c}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {sizes.length > 0 && (
                    <div className="mb-4">
                        <label className="font-sans text-[10px] uppercase tracking-[0.3em] text-sanct-rose block mb-2">Size</label>
                        <div className="flex flex-wrap gap-1.5">
                            {sizes.map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setSize(s)}
                                    data-testid={`wmwe-product-${product.id}-size-${s}`}
                                    className={`w-10 h-10 text-xs font-medium border rounded-full transition-colors ${
                                        size === s
                                            ? "bg-sanct-cocoa text-sanct-cream border-sanct-cocoa"
                                            : "bg-transparent text-sanct-cocoa border-sanct-cocoa/30 hover:border-sanct-cocoa"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={buy} className="mt-auto space-y-3">
                    <input
                        type="email"
                        name="email"
                        required
                        placeholder="your@email.com"
                        data-testid={`wmwe-product-${product.id}-email`}
                        className="w-full font-sans text-sm bg-transparent border-b border-sanct-cocoa/30 py-2.5 outline-none focus:border-sanct-rose text-sanct-cocoa placeholder:text-sanct-cocoa/40"
                    />
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid={`wmwe-product-${product.id}-buy-btn`}
                        className="group w-full inline-flex items-center justify-center gap-3 px-6 py-3.5 bg-sanct-cocoa text-sanct-cream text-xs uppercase tracking-[0.25em] hover:bg-sanct-rose transition-colors duration-300 rounded-full disabled:opacity-50"
                    >
                        {loading ? "Redirecting…" : "Buy Now"}
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                </form>
            </div>
        </article>
    );
}

export default function WMWEShop() {
    const [products, setProducts] = useState([]);

    useEffect(() => {
        axios.get(`${API}/shop/products?brand=wmwe`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
    }, []);

    const buy = async (product, { quantity, size, color, customer_email }) => {
        try {
            const { data } = await axios.post(`${API}/shop/checkout`, {
                product_id: product.id,
                quantity,
                size,
                color,
                customer_email,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (err) {
            const msg = err?.response?.data?.detail || "Checkout unavailable.";
            toast.error(typeof msg === "string" ? msg : "Checkout failed.");
        }
    };

    return (
        <main data-testid="wmwe-shop" className="bg-sanct-sand text-sanct-charcoal">
            <section className="py-28 md:py-36 bg-sanct-cream text-center">
                <div className="max-w-[820px] mx-auto px-6 md:px-10">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">Shop</p>
                    <h1 className="font-serif text-5xl md:text-7xl text-sanct-cocoa leading-[1] tracking-tight">
                        Wear the
                        <span className="block italic text-sanct-rose mt-2">fellowship.</span>
                    </h1>
                    <p className="font-sans text-base text-sanct-charcoal/70 mt-8 leading-[1.8]">
                        Soft cottons, quiet promises. Every piece is a wearable reminder that you belong to a table of women praying you through.
                    </p>
                </div>
            </section>

            {/* Products */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-10">Featured Pieces</p>
                    {products.length === 0 ? (
                        <p className="font-serif italic text-xl text-sanct-cocoa text-center py-16">Preparing the collection…</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {products.map((p) => (
                                <WMWEProductCard key={p.id} product={p} onBuy={buy} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Coming Soon */}
            <section className="py-24 md:py-32 bg-sanct-clay/30">
                <div className="max-w-[1200px] mx-auto px-6 md:px-10">
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-8 text-center">Coming Soon</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {soonItems.map((item, i) => (
                            <div key={item.name} className="bg-sanct-cream rounded-[24px] aspect-[4/3] p-8 flex flex-col justify-between border border-sanct-clay/40">
                                <span className="font-serif italic text-sanct-rose text-4xl">0{i + 1}</span>
                                <div>
                                    <p className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/60 mb-1">{item.category}</p>
                                    <p className="font-serif text-2xl text-sanct-cocoa leading-tight">{item.name}</p>
                                    <p className="font-serif italic text-sm text-sanct-charcoal/60 mt-3">Coming soon.</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 bg-sanct-cream text-center">
                <div className="max-w-[820px] mx-auto px-6 md:px-10">
                    <Package size={24} className="text-sanct-rose mx-auto mb-6" strokeWidth={1.5} />
                    <h2 className="font-serif italic text-4xl md:text-6xl text-sanct-cocoa leading-[1.05] tracking-tight">
                        Carry the fellowship <span className="text-sanct-rose">with you.</span>
                    </h2>
                    <p className="font-sans text-sm text-sanct-charcoal/70 mt-8 leading-[1.8]">
                        Physical items ship via USPS within 3–5 business days. Questions? Email <a href="mailto:shana@theconnectionguru.co" className="underline decoration-sanct-rose decoration-2 underline-offset-4">shana@theconnectionguru.co</a>.
                    </p>
                </div>
            </section>
        </main>
    );
}
